import { Link } from "react-router-dom";
import { contentLink, contentTypeName, type AnyContent } from "@/lib/content";
import { formatShortDate, formatViews } from "@/lib/format";

const badgeStyles: Record<string, string> = {
  news: "bg-primary-50 text-primary-700",
  movie: "bg-accent-100 text-accent-900",
  variety: "bg-secondary-100 text-secondary-900",
};

interface ContentCardProps {
  item: AnyContent;
  variant?: "grid" | "row";
  showSummaryAlways?: boolean;
}

export default function ContentCard({ item, variant = "grid", showSummaryAlways = false }: ContentCardProps) {
  const isRow = variant === "row";
  const summaryClass = showSummaryAlways
    ? "mt-2 text-xs leading-relaxed text-foreground-600 line-clamp-2 md:text-sm"
    : "mt-2 text-xs leading-relaxed text-foreground-600 line-clamp-2 opacity-100 transition-all duration-300 max-sm:opacity-100 lg:mt-0 lg:max-h-0 lg:opacity-0 lg:group-hover:mt-2 lg:group-hover:max-h-24 lg:group-hover:opacity-100";

  return (
    <Link
      to={contentLink(item)}
      className={[
        "group block cursor-pointer rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300",
        isRow ? "flex flex-col gap-4 p-3 sm:flex-row sm:items-stretch" : "overflow-hidden",
      ].join(" ")}
    >
      <div
        className={[
          "relative overflow-hidden bg-background-200",
          isRow ? "w-full shrink-0 rounded-md sm:w-56" : "w-full",
        ].join(" ")}
      >
        <div className={isRow ? "aspect-[16/10] w-full" : "aspect-[16/10] w-full"}>
          <img
            src={item.cover}
            alt={`${item.title} 封面`}
            title={`${item.title} - 红桃网娱乐资讯`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <span
          className={`absolute left-2.5 top-2.5 rounded-sm px-2 py-1 text-[11px] font-medium ${
            badgeStyles[item.type] ?? "bg-background-50 text-foreground-700"
          }`}
        >
          {contentTypeName(item)}
        </span>
        {item.type === "movie" && (
          <span className="absolute right-2.5 top-2.5 flex items-center gap-1 rounded-sm bg-foreground-950/75 px-1.5 py-1 font-label text-[11px] text-accent-300">
            <i className="ri-star-fill text-[10px]" />
            {item.rating.toFixed(1)}
          </span>
        )}
      </div>

      <div className={isRow ? "flex min-w-0 flex-1 flex-col justify-between py-1" : "p-4"}>
        <div>
          <h3
            className={[
              "font-heading font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600",
              isRow ? "text-base md:text-lg" : "text-[15px] line-clamp-2 md:text-base",
            ].join(" ")}
          >
            {item.title}
          </h3>
          <p className={summaryClass}>{item.summary}</p>
        </div>
        <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-400 ${isRow ? "mt-3" : "mt-3"}`}>
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" />
            {formatShortDate(item.publishedAt)}
          </span>
          {item.type === "news" && isRow && (
            <span className="flex items-center gap-1">
              <i className="ri-newspaper-line" />
              {item.source}
            </span>
          )}
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(item.views)}
          </span>
          <span className="hidden sm:flex items-center gap-1 truncate">
            <i className="ri-price-tag-3-line" />
            {item.tags[0]}
          </span>
        </div>
      </div>
    </Link>
  );
}